import AnalyticsEvent from './analytics-event';
import { PaymentMethodTerms } from '@monolith/legacy/components/cart/checkout/checkout.types';
import { UserDisplayEvent, UserClick } from '@ankorstore/events-tracking';

export const AriaEventSource = {
  PAYMENT: 'payment',
  SETTINGS: 'settings',
  CART: 'cart',
  CHECKOUT: 'checkout',
} as const;

export type AriaEventSource = (typeof AriaEventSource)[keyof typeof AriaEventSource];

export interface AriaPaymentProperties {
  cart_id?: string;
  payment_terms?: PaymentMethodTerms;
  has_existing_mandate: boolean;
  source?: AriaEventSource;
}

export interface AriaVerificationProperties extends AriaPaymentProperties {
  error_message?: string;
}

/** @deprecated Migrate to `UserClick` from `@ankorstore/events-tracking` for standardized format */
export class AriaPaymentMethodSelectedEvent extends AnalyticsEvent {
  public readonly name = 'Aria Payment Method Selected';

  constructor(public readonly properties: AriaPaymentProperties) {
    super();
  }
}

/** @deprecated Migrate to `UserClick` from `@ankorstore/events-tracking` for standardized format */
export class AriaVerificationStartedEvent extends AnalyticsEvent {
  public readonly name = 'Aria Verification Started';

  constructor(public readonly properties: AriaPaymentProperties) {
    super();
  }
}

/** @deprecated Migrate to `UserClick` from `@ankorstore/events-tracking` for standardized format */
export class AriaVerificationCompletedEvent extends AnalyticsEvent {
  public readonly name = 'Aria Verification Completed';

  constructor(public readonly properties: AriaPaymentProperties) {
    super();
  }
}

/** @deprecated Migrate to `ErrorDisplayEvent` from `@ankorstore/events-tracking` for standardized format */
export class AriaVerificationFailedEvent extends AnalyticsEvent {
  public readonly name = 'Aria Verification Failed';

  constructor(public readonly properties: AriaVerificationProperties) {
    super();
  }
}

/** @deprecated Migrate to `UserClick` from `@ankorstore/events-tracking` for standardized format */
export class AriaBankDetailsEditClickedEvent extends AnalyticsEvent {
  public readonly name = 'Aria Bank Details Edit Clicked';

  constructor(public readonly properties: Partial<AriaPaymentProperties> = {}) {
    super();
  }
}

export interface AriaIbanUpdateProperties {
  source: 'payment' | 'settings';
}

/** @deprecated Migrate to `UserClick` from `@ankorstore/events-tracking` for standardized format */
export class AriaIbanUpdateButtonClickedEvent extends AnalyticsEvent {
  public readonly name = 'Aria IBAN Update Button Clicked';

  constructor(public readonly properties: AriaIbanUpdateProperties) {
    super();
  }
}

/** @deprecated Migrate to `UserClick` from `@ankorstore/events-tracking` for standardized format */
export class AriaIbanUpdateProceedClickedEvent extends AnalyticsEvent {
  public readonly name = 'Aria IBAN Update Proceed Clicked';

  constructor(public readonly properties: AriaIbanUpdateProperties) {
    super();
  }
}

/** @deprecated Migrate to `UserClick` from `@ankorstore/events-tracking` for standardized format */
export class AriaIbanUpdateSuccessEvent extends AnalyticsEvent {
  public readonly name = 'Aria IBAN Update Success';

  constructor(public readonly properties: AriaIbanUpdateProperties) {
    super();
  }
}

export type FrozenCreditEventSource = 'header' | 'settings' | 'cart' | 'checkout' | 'payment';

export const FrozenCreditBannerViewedEvent = (source: FrozenCreditEventSource) =>
  UserDisplayEvent({ component: 'frozen_credit_banner', value: 'banner_viewed', source });

export const FrozenCreditPayNowClickedEvent = (source: FrozenCreditEventSource) =>
  UserClick({ component: 'frozen_credit_banner', action: 'pay_now_clicked', source });

export const FrozenCreditBannerDismissedEvent = (source: 'cart' | 'checkout' | 'payment') =>
  UserClick({ component: 'frozen_credit_banner', action: 'banner_dismissed', source });

export type AriaRefusedSource = 'settings' | 'cart';

export type AriaRefusalReason = 'business_identifier' | 'legal_information' | 'kyc_required' | 'kyc_in_review' | 'generic';

const ARIA_REFUSED_COMPONENT = 'aria_refused_card';

export const AriaRefusedCardViewedEvent = (source: AriaRefusedSource, refusal_reason: AriaRefusalReason) =>
  UserDisplayEvent({ component: ARIA_REFUSED_COMPONENT, value: 'card_viewed', source, refusal_reason });

export const AriaRefusedUpdateClickedEvent = (source: AriaRefusedSource, refusal_reason: AriaRefusalReason) =>
  UserClick({ component: ARIA_REFUSED_COMPONENT, action: 'update_now_clicked', source, refusal_reason });

export const AriaRefusedBiSubmittedEvent = (source: AriaRefusedSource, refusal_reason: AriaRefusalReason) =>
  UserClick({ component: ARIA_REFUSED_COMPONENT, action: 'bi_update_submitted', source, refusal_reason });

export const AriaRefusedBannerDismissedEvent = (refusal_reason: AriaRefusalReason) =>
  UserClick({ component: ARIA_REFUSED_COMPONENT, action: 'banner_dismissed', source: 'cart', refusal_reason });

// Legal-info (diffusion partielle) CTAs live only on the settings card — source and reason are
// fixed rather than parameterized since no caller ever passes a different value (YAGNI).
export const AriaRefusedLegalInfoUpdateStatusClickedEvent = () =>
  UserClick({
    component: ARIA_REFUSED_COMPONENT,
    action: 'update_status_clicked',
    source: 'settings',
    refusal_reason: 'legal_information',
  });

export const AriaRefusedLegalInfoKeepStatusClickedEvent = () =>
  UserClick({
    component: ARIA_REFUSED_COMPONENT,
    action: 'keep_status_clicked',
    source: 'settings',
    refusal_reason: 'legal_information',
  });

export const AriaRefusedLegalInfoKeepStatusConfirmedEvent = () =>
  UserClick({
    component: ARIA_REFUSED_COMPONENT,
    action: 'keep_status_confirmed',
    source: 'settings',
    refusal_reason: 'legal_information',
  });

// KYC-required only renders on the settings card, so source is fixed here too.
export const AriaRefusedKycStartedEvent = () =>
  UserClick({
    component: ARIA_REFUSED_COMPONENT,
    action: 'kyc_started',
    source: 'settings',
    refusal_reason: 'kyc_required',
  });

export type CreditLimitPillState =
  | 'refused'
  | 'frozen'
  | 'incomplete'
  | 'kyc_required'
  | 'kyc_in_review'
  | 'active'
  // Ankorstore's own payment-terms verification (RCN-498) — distinct from the Aria KYC states above.
  | 'business_verification_required'
  | 'business_verification_in_review'
  | 'business_verification_escalated'
  | 'business_verification_rejected';

export const CreditLimitPillViewedEvent = (pill_state: CreditLimitPillState) =>
  UserDisplayEvent({ component: 'credit_limit_pill', value: 'pill_viewed', pill_state });
